import { useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { IFlattenLandingElement, INormalizedLandingElement } from '~/src/services/projects';
import { IPreparedBlock } from '~/src/services/blocks/preparePreview';
import { CSSPropWithBreakpoints, GlobalStyle, GlobalWrapper } from './styled';
import { LandingContext } from '~/src/containers/ContextContainer';
import { ICityData } from '~/src/components/LandingBlock/Header/HeaderPanel';
import { IMG_DOMAIN } from '~/src/services/blocks/templates';
import { StickyElementForPortal } from '~/src/components/StickyElementForPortal';
import { useStickyElementContext } from '~/src/components/StickyElementForPortal/useStickyElementContext';

const BlockGrid = dynamic(() => import('~/src/components/LandingBlock/BlockGrid'));
const Footer = dynamic(() => import('~/src/components/LandingBlock/Footer'));
const Header = dynamic(() => import('./Header'));
const Grid = dynamic(() => import('./Grid'));
const Shift = dynamic(() => import('./Shift'));
const BgBlock = dynamic(() => import('./BgBlock'));
const Callback = dynamic(() => import('./Callback'));
const Ligal = dynamic(() => import('./Ligal'));
const InfoBlock = dynamic(() => import('./InfoBlock'));
const TextBlock = dynamic(() => import('./TextBlock'));
const Slider = dynamic(() => import('./Slider'), { ssr: false });
const TariffCardsRA = dynamic(() => import('./TariffCardsRA'));
const Tariffs = dynamic(() => import('./Tariffs'));
const CarouselBlock = dynamic(() => import('./CarouselBlock'));
const ImageGrid = dynamic(() => import('./ImageGrid'));
const ImageTextBlock = dynamic(() => import('./ImageTextBlock'));
const TabsBlock = dynamic(() => import('./Tabs'));
const Accordion = dynamic(() => import('./Accordion'));
const BlockSlider = dynamic(() => import('./BlockSlider'));
const BlockSliderNuka = dynamic(() => import('~/src/components/LandingBlock/BlockSliderNuka'));

export interface ILandingBlock {
    block: IFlattenLandingElement | Record<string, never>;
    items: INormalizedLandingElement;
    customStyles: {
        [key: string]: CSSPropWithBreakpoints;
    },
    enableWebp: boolean;
    isAdmin?: boolean,
    currentBreakpoint?: string;
    alias?: string;
    cityData?: ICityData;
}

/**
 * Убирает квадратные скобки в id ( Shift[18] => Shift-18 )
 */
const formatIdForHTML = (id = '') => {
    return id.replace(/\[([^\]]+)\]/, '-$1');
};

const SwitchLandingBlocks = ({
                                 block,
                                 items,
                                 customStyles,
                                 enableWebp,
                                 isAdmin = true,
                                 currentBreakpoint,
                                 alias = '',
                                 cityData,
                             }: ILandingBlock): JSX.Element => {
    const blockId = block.id;
    const preparedBackground = block.properties.background
        ? `${IMG_DOMAIN}${enableWebp ? block.properties.backgroundWebp : block.properties.background}`
        : '';

    const additionalBlockConfig = useMemo(() => {
        return {
            marginTop: (typeof block.properties['marginTop'] === 'string')
                ? { desktop: block.properties['marginTop'] }
                : block.properties['marginTop'],
            marginBottom: (typeof block.properties['marginBottom'] === 'string')
                ? { desktop: block.properties['marginBottom'] }
                : block.properties['marginBottom'],
            blockWidth: (typeof block.properties['blockWidth'] === 'string')
                ? { desktop: block.properties['blockWidth'] }
                : block.properties['blockWidth'],
        };
    }, [block.properties]);

    switch (block.renderId) {
        case 'Header1':
        case 'Header2':
            return (
                <Header
                    blockId={formatIdForHTML(blockId)}
                    title={items[`${blockId}.Title`].properties['content']}
                    subtitle={items[`${blockId}.SubTitle`].properties['content']}
                    text={items[`${blockId}.Text`].properties['htmlContent']}
                    logo={{
                        src: items[`${blockId}.Logo`]?.properties['content'],
                        alt: items[`${blockId}.Logo`]?.properties['alt'],
                        webp: items[`${blockId}.Logo`]?.properties['webp'],
                    }}
                    picture={{
                        src: items[`${blockId}.Image`].properties['content'],
                        alt: items[`${blockId}.Image`].properties['alt'],
                        webp: items[`${blockId}.Image`].properties['webp'],
                    }}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`].properties['content'],
                        href: items[`${blockId}.Button`].properties['href'],
                        idForScroll: items[`${blockId}.Button`].properties['idForScroll'],
                        isTargetBlank: items[`${blockId}.Button`].properties['isTargetBlank'],
                        enableCalltouch: items[`${blockId}.Button`].properties['enableCalltouch'],
                        analytics: items[`${blockId}.Button`].properties['analytics'],
                        backgroundColor: items[`${blockId}.Button`].properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`].properties['backgroundHoverColor'],
                    }}
                    form={block.renderId === 'Header2'
                        ? {
                            globalEnsId: items[`${blockId}.PhoneForm`]?.properties['customize']?.globalEnsId,
                            productId: items[`${blockId}.PhoneForm`]?.properties['customize']?.productId,
                            formWidth: items[`${blockId}.PhoneForm`]?.properties['formWidth'],
                            formHeight: items[`${blockId}.PhoneForm`]?.properties['formHeight'],
                            phoneFormWidth: items[`${blockId}.PhoneForm`]?.properties['phoneFormWidth'],
                            phonePlaceholder: items[`${blockId}.PhoneForm`]?.properties['phonePlaceholder'],
                            popupTitleSuccess: items[`${blockId}.PhoneForm`]?.properties['popupTitleSuccess'],
                            popupTitleError: items[`${blockId}.PhoneForm`]?.properties['popupTitleError'],
                            isBottomSticky: items[`${blockId}.PhoneForm`]?.properties['isBottomSticky'],
                            buttonProps: {
                                buttonText: items[`${blockId}.PhoneForm.Button.Text`]?.properties['content'] ?? items[`${blockId}.PhoneForm`]?.properties['buttonText'],
                                href: items[`${blockId}.PhoneForm.Button`]?.properties['href'],
                                idForScroll: items[`${blockId}.PhoneForm.Button`]?.properties['idForScroll'],
                                isTargetBlank: items[`${blockId}.PhoneForm.Button`]?.properties['isTargetBlank'],
                                enableCalltouch: items[`${blockId}.PhoneForm.Button`]?.properties['enableCalltouch'],
                                analytics: items[`${blockId}.PhoneForm.Button`]?.properties['analytics'],
                                backgroundColor: items[`${blockId}.PhoneForm.Button`]?.properties['backgroundColor'],
                                backgroundHoverColor: items[`${blockId}.PhoneForm.Button`]?.properties['backgroundHoverColor'],
                            },
                            checkboxFontSize: items[`${blockId}.PhoneForm`]?.properties.checkboxProperties?.['font'],
                            checkboxFontColor: items[`${blockId}.PhoneForm`]?.properties.checkboxProperties?.['color'],
                        }
                        : undefined
                    }
                    analytics={block.renderId === 'Header2'
                        ? items[`${blockId}.PhoneForm`]?.properties['analytics']
                        : undefined
                    }
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    cityData={cityData}
                    background={preparedBackground}
                    alias={alias}
                    additionalBlockConfig={{
                        ...additionalBlockConfig,
                        ...{ imagePlacement: block.properties['imagePlacement'] },
                        ...{ offsetX: items[`${blockId}.Image`].properties['offsetX'] },
                        ...{ offsetY: items[`${blockId}.Image`].properties['offsetY'] },
                    }}
                    ageRestriction={{
                        active: items[`${blockId}.AgeRestriction`]?.properties['active'] ?? false,
                        backgroundColor: items[`${blockId}.AgeRestriction`]?.properties['backgroundColor'],
                        image: items[`${blockId}.AgeRestriction.Image`]?.properties['content'],
                        webp: items[`${blockId}.AgeRestriction.Image`]?.properties['webp'],
                    }}
                    isAutoHeightSize={block.properties['isAutoHeightSize']}
                    direction={block.properties['isVertical'] ? 'vertical' : 'horizontal'}
                    titleTag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                    subtitleTag={items[`${blockId}.SubTitle`]?.properties['customize']?.tag}
                />
            );
        case 'InfoBlock':
            return (
                <InfoBlock
                    blockId={formatIdForHTML(blockId)}
                    isAdmin={isAdmin}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    subtitle={items[`${blockId}.SubTitle`].properties['content'] ?? items[`${blockId}.SubTitle`].properties['htmlContent']}
                    text={items[`${blockId}.Text`].properties['htmlContent']}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`].properties['content'],
                        href: items[`${blockId}.Button`].properties['href'],
                        idForScroll: items[`${blockId}.Button`].properties['idForScroll'],
                        isTargetBlank: items[`${blockId}.Button`].properties['isTargetBlank'],
                        enableCalltouch: items[`${blockId}.Button`].properties['enableCalltouch'],
                        analytics: items[`${blockId}.Button`].properties['analytics'],
                        backgroundColor: items[`${blockId}.Button`].properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`].properties['backgroundHoverColor'],
                    }}
                    picture={{
                        src: items[`${blockId}.Image`].properties['content'],
                        webp: items[`${blockId}.Image`].properties['webp'],
                        alt: items[`${blockId}.Image`].properties['alt'],
                        paddingTop: items[`${blockId}.Image`].properties.offsetY?.desktop,
                        paddingRight: items[`${blockId}.Image`].properties.offsetX?.desktop,
                    }}
                    customStyles={customStyles}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    titleTag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                    subtitleTag={items[`${blockId}.SubTitle`]?.properties['customize']?.tag}
                />
            );
        case 'Grid':
        case 'Grid2':
            return (
                <Grid
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    buttonProps={{
                        buttonText: items[`${blockId}.BlockButton.Text`]?.properties['content'],
                        href: items[`${blockId}.BlockButton`]?.properties['href'],
                        idForScroll: items[`${blockId}.BlockButton`]?.properties['idForScroll'],
                        isTargetBlank: items[`${blockId}.BlockButton`]?.properties['isTargetBlank'],
                        enableCalltouch: items[`${blockId}.BlockButton`]?.properties['enableCalltouch'],
                        analytics: items[`${blockId}.BlockButton`]?.properties['analytics'],
                        backgroundColor: items[`${blockId}.BlockButton`]?.properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.BlockButton`]?.properties['backgroundHoverColor'],
                    }}
                    columnCount={block.properties['columnCount']}
                    columns={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Header`]?.properties['customize']?.tag}
                />
            );
        case 'Shift':
            return (
                <Shift
                    blockId={formatIdForHTML(blockId)}
                    sections={items}
                    fixed={block.properties['fixedImages']}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                />
            );
        case 'BgBlock':
            return (
                <BgBlock
                    blockId={formatIdForHTML(blockId)}
                    title={items[`${blockId}.Title`].properties['content']}
                    desc={items[`${blockId}.Description`].properties['content']}
                    customStyles={customStyles}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                />
            );
        case 'Callback':
            return (
                <Callback
                    blockId={formatIdForHTML(blockId)}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    subtitle={items[`${blockId}.SubTitle`].properties['content'] ?? items[`${blockId}.SubTitle`].properties['htmlContent']}
                    form={{
                        globalEnsId: items[`${blockId}.PhoneForm`]?.properties['customize']?.globalEnsId,
                        productId: items[`${blockId}.PhoneForm`]?.properties['customize']?.productId,
                        formWidth: items[`${blockId}.PhoneForm`]?.properties['formWidth'],
                        formHeight: items[`${blockId}.PhoneForm`]?.properties['formHeight'],
                        phoneFormWidth: items[`${blockId}.PhoneForm`]?.properties['phoneFormWidth'],
                        phonePlaceholder: items[`${blockId}.PhoneForm`]?.properties['phonePlaceholder'],
                        popupTitleSuccess: items[`${blockId}.PhoneForm`]?.properties['popupTitleSuccess'],
                        popupTitleError: items[`${blockId}.PhoneForm`]?.properties['popupTitleError'],
                        buttonProps: {
                            buttonText: items[`${blockId}.PhoneForm.Button.Text`]?.properties['content'] ?? items[`${blockId}.PhoneForm`]?.properties['buttonText'],
                            href: items[`${blockId}.PhoneForm.Button`]?.properties['href'],
                            idForScroll: items[`${blockId}.PhoneForm.Button`]?.properties['idForScroll'],
                            isTargetBlank: items[`${blockId}.PhoneForm.Button`]?.properties['isTargetBlank'],
                            enableCalltouch: items[`${blockId}.PhoneForm.Button`]?.properties['enableCalltouch'],
                            analytics: items[`${blockId}.PhoneForm.Button`]?.properties['analytics'],
                            backgroundColor: items[`${blockId}.PhoneForm.Button`]?.properties['backgroundColor'],
                            backgroundHoverColor: items[`${blockId}.PhoneForm.Button`]?.properties['backgroundHoverColor'],
                        },
                        checkboxFontSize: items[`${blockId}.PhoneForm`]?.properties.checkboxProperties?.['font'],
                        checkboxFontColor: items[`${blockId}.PhoneForm`]?.properties.checkboxProperties?.['color'],
                    }}
                    isAdmin={isAdmin}
                    analytics={items[`${blockId}.PhoneForm`]?.properties['analytics']}
                    progress={[
                        items[`${blockId}.Instruction.Step1`].properties['htmlContent'] ?? items[`${blockId}.Instruction.Step1`].properties['content'],
                        items[`${blockId}.Instruction.Step2`].properties['htmlContent'] ?? items[`${blockId}.Instruction.Step2`].properties['content'],
                        items[`${blockId}.Instruction.Step3`].properties['htmlContent'] ?? items[`${blockId}.Instruction.Step3`].properties['content'],
                    ]}
                    customStyles={customStyles}
                    cityData={cityData}
                    background={preparedBackground}
                    alias={alias}
                    additionalBlockConfig={additionalBlockConfig}
                    titleTag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                    subtitleTag={items[`${blockId}.SubTitle`]?.properties['customize']?.tag}
                />
            );
        case 'Ligal':
            return (
                <Ligal
                    blockId={formatIdForHTML(blockId)}
                    ligalColumnCount={block.properties['ligalColumnCount']}
                    columns={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    cityData={cityData}
                    additionalBlockConfig={additionalBlockConfig}
                />
            );
        case 'TextBlock':
            return (
                <TextBlock
                    blockId={formatIdForHTML(blockId)}
                    text={items[`${blockId}.Text`].properties['htmlContent']}
                    customStyles={customStyles}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Text`]?.properties['customize']?.tag}
                />
            );
        case 'Footer1':
        case 'Footer2':
            return (
                <Footer
                    blockId={formatIdForHTML(blockId)}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    subtitle={items[`${blockId}.SubTitle`].properties['content'] ?? items[`${blockId}.SubTitle`].properties['htmlContent']}
                    picture={{
                        src: items[`${blockId}.Image`]?.properties['content'],
                        webp: items[`${blockId}.Image`]?.properties['webp'],
                        alt: items[`${blockId}.Image`]?.properties['alt'],
                    }}
                    installmentForm={{
                        alias: items[`${blockId}.Form`]?.properties['customize']?.alias,
                        displayPrice: items[`${blockId}.Form`]?.properties['displayPrice'],
                        globalEnsId: items[`${blockId}.Form`]?.properties['customize']?.globalEnsId,
                        productId: items[`${blockId}.Form`]?.properties['customize']?.productId,
                        namePlaceholder: items[`${blockId}.Form`]?.properties['namePlaceholder'],
                        phonePlaceholder: items[`${blockId}.Form`]?.properties['phonePlaceholder'],
                        popupTitleSuccess: items[`${blockId}.Form`]?.properties['popupTitleSuccess'],
                        popupTitleError: items[`${blockId}.Form`]?.properties['popupTitleError'],
                        buttonProps: {
                            buttonText: items[`${blockId}.Form.Button.Text`]?.properties['content'] ?? items[`${blockId}.Form`]?.properties['buttonText'],
                            href: items[`${blockId}.Form.Button`]?.properties['href'],
                            idForScroll: items[`${blockId}.Form.Button`]?.properties['idForScroll'],
                            isTargetBlank: items[`${blockId}.Form.Button`]?.properties['isTargetBlank'],
                            enableCalltouch: items[`${blockId}.Form.Button`]?.properties['enableCalltouch'],
                            analytics: items[`${blockId}.Form.Button`]?.properties['analytics'],
                            backgroundColor: items[`${blockId}.Form.Button`]?.properties['backgroundColor'],
                            backgroundHoverColor: items[`${blockId}.Form.Button`]?.properties['backgroundHoverColor'],
                        },
                        checkboxFontSize: items[`${blockId}.Form`]?.properties.checkboxProperties?.['font'],
                        checkboxFontColor: items[`${blockId}.Form`]?.properties.checkboxProperties?.['color'],
                    }}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    analytics={items[`${blockId}.Form`]?.properties['analytics']}
                    background={preparedBackground}
                    alias={alias}
                    additionalBlockConfig={additionalBlockConfig}
                    titleTag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                    subtitleTag={items[`${blockId}.SubTitle`]?.properties['customize']?.tag}
                />
            );
        case 'Slider':
            return (
                <Slider
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    columns={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Header`]?.properties['customize']?.tag}
                />
            );
        case 'TariffCardsRA':
            return (
                <TariffCardsRA
                    blockId={formatIdForHTML(blockId)}
                    cityDomain={cityData?.city.domain}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    topPrice={items[`${blockId}.TopPrice`].properties['content']}
                    topText={items[`${blockId}.TopText`].properties['content']}
                    bottomPrice={items[`${blockId}.BottomPrice`].properties['content']}
                    bottomText={items[`${blockId}.BottomText`].properties['content']}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`].properties['content'],
                        href: items[`${blockId}.Button`].properties['href'],
                        isTargetBlank: items[`${blockId}.Button`].properties['isTargetBlank'],
                        analytics: items[`${blockId}.Button`].properties['analytics'],
                        idForScroll: items[`${blockId}.Button`].properties['idForScroll'],
                        backgroundColor: items[`${blockId}.Button`].properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`].properties['backgroundHoverColor'],
                    }}
                    showBundles={block.properties['showBundles']}
                    showDiscount={block.properties['showDiscount']}
                    bundlesAliases={block.properties['bundlesAliases']}
                    showInternet={block.properties['showInternet']}
                    internetAliases={block.properties['internetAliases']}
                    aliasWildcard={block.properties['aliasWildcard']}
                    customStyles={customStyles}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                />
            );
        case 'Tariffs':
            return (
                <Tariffs
                    blockId={formatIdForHTML(blockId)}
                    cityDomain={cityData?.city.domain}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`].properties['content'],
                        href: items[`${blockId}.Button`].properties['href'],
                        isTargetBlank: items[`${blockId}.Button`].properties['isTargetBlank'],
                        analytics: items[`${blockId}.Button`].properties['analytics'],
                        idForScroll: items[`${blockId}.Button`].properties['idForScroll'],
                        backgroundColor: items[`${blockId}.Button`].properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`].properties['backgroundHoverColor'],
                    }}
                    showBundles={block.properties['showBundles']}
                    isCarouselActive={block.properties['isCarouselActive']}
                    isFullByActive={block.properties['isFullByActive']}
                    bundlesAliases={block.properties['bundlesAliases']}
                    showInternet={block.properties['showInternet']}
                    internetAliases={block.properties['internetAliases']}
                    aliasWildcard={block.properties['aliasWildcard']}
                    customStyles={customStyles}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                />
            );
        case 'Carousel':
            return (
                <CarouselBlock
                    blockId={formatIdForHTML(blockId)}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    subTitle={items[`${blockId}.SubTitle`].properties['content'] ?? items[`${blockId}.SubTitle`].properties['htmlContent']}
                    text={items[`${blockId}.Text`].properties['htmlContent'] ?? items[`${blockId}.Text`].properties['content']}
                    items={items}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    customStyles={customStyles}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`].properties['content'],
                        href: items[`${blockId}.Button`].properties['href'],
                        idForScroll: items[`${blockId}.Button`].properties['idForScroll'],
                        isTargetBlank: items[`${blockId}.Button`].properties['isTargetBlank'],
                        enableCalltouch: items[`${blockId}.Button`].properties['enableCalltouch'],
                        analytics: items[`${blockId}.Button`].properties['analytics'],
                        backgroundColor: items[`${blockId}.Button`].properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`].properties['backgroundHoverColor'],
                    }}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    titleTag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                    subtitleTag={items[`${blockId}.SubTitle`]?.properties['customize']?.tag}
                />
            );
        case 'ImageGrid':
            return (
                <ImageGrid
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    imageColumnCount={block.properties['imageColumnCount']}
                    columns={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`]?.properties['content'],
                        href: items[`${blockId}.Button`]?.properties['href'],
                        idForScroll: items[`${blockId}.Button`]?.properties['idForScroll'],
                        isTargetBlank: items[`${blockId}.Button`]?.properties['isTargetBlank'],
                        enableCalltouch: items[`${blockId}.Button`]?.properties['enableCalltouch'],
                        analytics: items[`${blockId}.Button`]?.properties['analytics'],
                        backgroundColor: items[`${blockId}.Button`]?.properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`]?.properties['backgroundHoverColor'],
                    }}
                    text={items[`${blockId}.Text`].properties}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Header`]?.properties['customize']?.tag}
                />
            );
        case 'ImageTextBlock':
            return (
                <ImageTextBlock
                    blockId={formatIdForHTML(blockId)}
                    title={items[`${blockId}.Title`].properties['content'] ?? items[`${blockId}.Title`].properties['htmlContent']}
                    subtitle={items[`${blockId}.SubTitle`]?.properties['content'] ?? items[`${blockId}.SubTitle`]?.properties['htmlContent']}
                    text={items[`${blockId}.Text`].properties['htmlContent']}
                    customStyles={customStyles}
                    background={preparedBackground}
                    leftImage={{
                        src: items[`${blockId}.LeftImage`].properties['content'],
                        webp: items[`${blockId}.LeftImage`].properties['webp'],
                        alt: items[`${blockId}.LeftImage`].properties['alt'],
                    }}
                    rightImage={{
                        src: items[`${blockId}.RightImage`].properties['content'],
                        webp: items[`${blockId}.RightImage`].properties['webp'],
                        alt: items[`${blockId}.RightImage`].properties['alt'],
                    }}
                    buttonProps={{
                        buttonText: items[`${blockId}.Button.Text`]?.properties['content'],
                        href: items[`${blockId}.Button`]?.properties['href'],
                        idForScroll: items[`${blockId}.Button`]?.properties['idForScroll'],
                        isTargetBlank: items[`${blockId}.Button`]?.properties['isTargetBlank'],
                        enableCalltouch: items[`${blockId}.Button`]?.properties['enableCalltouch'],
                        analytics: items[`${blockId}.Button`]?.properties['analytics'],
                        backgroundColor: items[`${blockId}.Button`]?.properties['backgroundColor'],
                        backgroundHoverColor: items[`${blockId}.Button`]?.properties['backgroundHoverColor'],
                    }}
                    additionalBlockConfig={additionalBlockConfig}
                    leftImageProperties={{
                        width: items[`${blockId}.LeftImage`].properties.customize?.width,
                        height: items[`${blockId}.LeftImage`].properties.customize?.height,
                        alignment: items[`${blockId}.LeftImage`].properties.customize?.justifyContent,
                        paddingTop: items[`${blockId}.LeftImage`].properties.offsetY?.desktop,
                        paddingRight: items[`${blockId}.LeftImage`].properties.offsetX?.desktop,

                    }}
                    rightImageProperties={{
                        width: items[`${blockId}.RightImage`].properties.customize?.width,
                        height: items[`${blockId}.RightImage`].properties.customize?.height,
                        alignment: items[`${blockId}.RightImage`].properties.customize?.justifyContent,
                        paddingTop: items[`${blockId}.RightImage`].properties.offsetY?.desktop,
                        paddingRight: items[`${blockId}.RightImage`].properties.offsetX?.desktop,

                    }}
                    titleTag={items[`${blockId}.Title`]?.properties['customize']?.tag}
                    subtitleTag={items[`${blockId}.SubTitle`]?.properties['customize']?.tag}
                />
            );
        case 'Accordion':
            return (
                <Accordion
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    columns={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    isDarkTheme={block.properties.isDarkTheme ?? false}
                    tag={items[`${blockId}.Header`]?.properties['customize']?.tag}
                />
            );
        case 'Tabs':
            return (
                <TabsBlock
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    tabs={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    alignTabs={block.properties.alignTabs}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                />
            );
        case 'BlockSlider':
            return (
                <BlockSlider
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    blocks={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Header`]?.properties['customize']?.tag}
                />
            );
        case 'BlockSliderNuka':
            return (
                <BlockSliderNuka
                    blockId={formatIdForHTML(blockId)}
                    header={items[`${blockId}.Header`].properties['content'] ?? items[`${blockId}.Header`].properties['htmlContent']}
                    blocks={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    backgroundColor={`${block.properties.backgroundColor}`}
                    additionalBlockConfig={additionalBlockConfig}
                    tag={items[`${blockId}.Header`]?.properties['customize']?.tag}
                />
            );
        case 'BlockGrid':
            return (
                <BlockGrid
                    blockId={formatIdForHTML(blockId)}
                    columns={items}
                    customStyles={customStyles}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    background={preparedBackground}
                    additionalBlockConfig={additionalBlockConfig}
                />
            );
        default:
            return null;
    }
};

interface IBlocks {
    blocks: IPreparedBlock[];
    isAdmin?: boolean;
    currentBreakpoint?: string;
    styles?: string;
    background?: string;
    alias: string;
}

const LandingBlocks = ({
                           blocks,
                           isAdmin = true,
                           currentBreakpoint,
                           styles = '',
                           background = '',
                           alias,
                       }: IBlocks): JSX.Element => {
    const { city, enableWebp } = useContext(LandingContext);
    const { portalElementContext, PortalElementContextProvider } = useStickyElementContext();

    const { query } = useRouter();

    useEffect(() => {
        if (query.scrollTo) {
            setTimeout(() => {
                const element = document.querySelector(`#${query.scrollTo}`);
                if (element) {
                    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            }, 0);
        }
    }, [query]);

    return (
        <GlobalWrapper styles={styles} background={background}>
            {!isAdmin &&
                <noscript
                    dangerouslySetInnerHTML={{
                        __html: `
                            <!-- Google Tag Manager (noscript) -->
                            <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-M43KXLC"
                            height="0" width="0" style="display:none;visibility:hidden"></iframe>
                            <!-- End Google Tag Manager (noscript) -->
                        `,
                    }}
                />}
            <GlobalStyle/>
            <PortalElementContextProvider value={portalElementContext}>
            {blocks?.map(item => (
                <SwitchLandingBlocks
                    key={item.key}
                    block={item.block}
                    items={item.items}
                    customStyles={item.customStyles}
                    enableWebp={enableWebp}
                    isAdmin={isAdmin}
                    currentBreakpoint={currentBreakpoint}
                    alias={alias}
                    cityData={{ city }}
                />
            ))}
            <StickyElementForPortal/>
            </PortalElementContextProvider>
        </GlobalWrapper>
    );
};

export default LandingBlocks;
